import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react'

/**
 * Browser translation (Chrome's Translate, Edge's, extensions) rewrites text nodes under
 * React's feet. React then fails with DOM errors like "Failed to execute 'removeChild' on
 * 'Node'". Those are not bugs in the page: remounting the subtree rebuilds it from state.
 */
export function isDomMutationError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return (
    error.name === 'NotFoundError' ||
    /removeChild|insertBefore|not a child of this node|The node to be removed/i.test(error.message)
  )
}

const MAX_RECOVERIES = 3
const RECOVERY_WINDOW_MS = 10_000

interface Props {
  children: ReactNode
  /** Changing this (e.g. the route) clears a shown error. */
  resetKey: string
  fallback: (error: Error) => ReactNode
}

interface State {
  error: Error | null
  /** Bumped to remount children after a translation-induced DOM error. */
  generation: number
}

export class RecoveringErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, generation: 0 }
  private recoveries: number[] = []

  static getDerivedStateFromError(error: unknown): Partial<State> {
    if (isDomMutationError(error)) return { generation: Date.now() }
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (!isDomMutationError(error)) {
      console.error('Galley screen error', error, info.componentStack)
      return
    }
    const now = Date.now()
    this.recoveries = this.recoveries.filter((t) => now - t < RECOVERY_WINDOW_MS)
    this.recoveries.push(now)
    // Something keeps breaking the DOM: stop remounting and show the error page instead.
    if (this.recoveries.length > MAX_RECOVERIES) {
      this.setState({ error: error instanceof Error ? error : new Error(String(error)) })
    }
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null })
  }

  override render() {
    if (this.state.error) return this.props.fallback(this.state.error)
    return <Fragment key={this.state.generation}>{this.props.children}</Fragment>
  }
}
